import { useCallback, useEffect, useState, type FC } from 'react';
import {
  ComposedModal, IconButton, InlineLoading, InlineNotification, ModalBody, ModalHeader, Theme,
} from '@carbon/react';
import { ChevronLeft, ChevronRight, Download, Launch } from '@carbon/icons-react';

import { LeitorDePdf } from './LeitorDePdf';
import { ROTULOS, rotuloDoAviso } from './rotulos';
import styles from './VisualizadorDeArquivo.module.scss';

// VER UM ARQUIVO SEM BAIXAR — imagem, vídeo e PDF num `ComposedModal` grande, numa zona `g100` do
// Carbon (o escuro é o fundo de quem olha uma mídia). No cabeçalho, o tipo, o nome, o tamanho e a
// posição na lista; na barra, anterior e próximo (entre os itens da lista aberta, também pelas
// setas do teclado nas imagens), abrir numa aba nova (PDF) e baixar. O PDF é desenhado pelo pdf.js.

export type TipoVisualizavel = 'imagem' | 'video' | 'pdf';

export interface ItemDoVisualizador {
  id: string;
  tipo: TipoVisualizavel;
  /** O endereço para mostrar. Nulo enquanto ainda está sendo preparado (o PDF é baixado antes). */
  url: string | null;
  nome: string;
  /** Tipo e tamanho ("Vídeo · 8 MB"). */
  apoio?: string;
}

const ROTULO: Record<TipoVisualizavel, string> = { imagem: 'Imagem', video: 'Vídeo', pdf: 'Documento' };

export const VisualizadorDeArquivo: FC<{
  itens: ItemDoVisualizador[];
  /** O id do item aberto; `null` fecha. */
  aberto: string | null;
  trocar: (id: string) => void;
  fechar: () => void;
  baixar?: (id: string) => void;
  abrirFora?: (id: string) => void;
}> = ({ itens, aberto, trocar, fechar, baixar, abrirFora }) => {
  const indice = itens.findIndex((i) => i.id === aberto);
  const atual = indice >= 0 ? itens[indice] : null;
  const varios = itens.length > 1;
  const [semSuporte, setSemSuporte] = useState(false);
  useEffect(() => setSemSuporte(false), [aberto]);

  const passo = useCallback((delta: number) => {
    if (!varios || indice < 0) return;
    trocar(itens[(indice + delta + itens.length) % itens.length].id);
  }, [itens, indice, trocar, varios]);

  useEffect(() => {
    if (!atual || atual.tipo !== 'imagem') return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') passo(1);
      if (e.key === 'ArrowLeft') passo(-1);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [atual, passo]);

  const conteudo = () => {
    if (!atual) return null;
    if (!atual.url) return <InlineLoading description='Abrindo…' iconDescription={ROTULOS.carregando} />;
    if (atual.tipo === 'imagem') return <img src={atual.url} alt={atual.nome} className={styles.imagem} />;
    if (atual.tipo === 'video') {
      return semSuporte ? (
        <InlineNotification
          kind='warning'
          lowContrast
          hideCloseButton
          title='O navegador não reproduz este formato de vídeo.'
          subtitle='Baixe o arquivo para assistir.'
          statusIconDescription={rotuloDoAviso('warning')}
          aria-label={ROTULOS.fecharAviso}
        />
      ) : (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={atual.id}
          src={atual.url}
          className={styles.video}
          controls
          autoPlay
          playsInline
          onError={() => setSemSuporte(true)}
          aria-label={atual.nome}
        />
      );
    }
    return <LeitorDePdf key={atual.id} url={atual.url} nome={atual.nome} />;
  };

  const apoio = atual ? [atual.apoio, varios ? `${indice + 1} de ${itens.length}` : null].filter(Boolean).join(' · ') : '';

  return (
    <Theme theme='g100'>
      <ComposedModal
        open={!!atual}
        onClose={fechar}
        size='lg'
        aria-label={atual ? `${ROTULO[atual.tipo]} ${atual.nome}` : undefined}
      >
        {/* O cabeçalho é filho DIRETO do ComposedModal: é assim que o Carbon liga o X ao `onClose`.
            Dentro de um fragmento o X não fecha nada. */}
        <ModalHeader
          label={atual ? `${ROTULO[atual.tipo]}${apoio ? ` · ${apoio}` : ''}` : ''}
          title={atual?.nome ?? ''}
          iconDescription={ROTULOS.fechar}
        />
        {atual && (
          <ModalBody className={styles.corpo}>
            <div className={styles.barra}>
              {varios && (
                <>
                  <IconButton kind='ghost' label='Anterior' onClick={() => passo(-1)}><ChevronLeft /></IconButton>
                  <IconButton kind='ghost' label='Próximo' onClick={() => passo(1)}><ChevronRight /></IconButton>
                </>
              )}
              {abrirFora && atual.tipo === 'pdf' && (
                <IconButton kind='ghost' label='Abrir em nova aba' onClick={() => abrirFora(atual.id)}><Launch /></IconButton>
              )}
              {baixar && (
                <IconButton kind='ghost' label='Baixar' align='bottom-end' onClick={() => baixar(atual.id)}><Download /></IconButton>
              )}
            </div>
            <figure className={styles.palco}>{conteudo()}</figure>
          </ModalBody>
        )}
      </ComposedModal>
    </Theme>
  );
};
